import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../../../configuration/config';

const PeriodicMaintenance = () => {
    const { i18n } = useTranslation(['info', 'navbar']);
    const lang = i18n.language || 'ar';
    const isRtl = lang === 'ar';

    const [maintenanceData, setMaintenanceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(0);

    const fetchMaintenanceData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${config.controllURL}${lang}/api/Pages/fn_GetMaintenanceData`, {
                "MaintenanceId": null,
                "Language": lang === 'ar' ? 'ar' : 'en'
            });

            if (response.data) {
                setMaintenanceData(response.data);
                const firstDetail = response.data.find(item => item.DataType === 'Detail');
                if (firstDetail) {
                    setActiveTab(firstDetail.GUID);
                }
            }
        } catch (error) {
            console.error('Error fetching maintenance data:', error);
        } finally {
            setLoading(false);
        }
    }, [lang]);

    useEffect(() => {
        fetchMaintenanceData();
    }, [fetchMaintenanceData]);

    const detailItems = useMemo(() => maintenanceData.filter(item => item.DataType === 'Detail'), [maintenanceData]);
    const noteItems = useMemo(() => maintenanceData.filter(item => item.DataType === 'Note'), [maintenanceData]);

    const activeItem = useMemo(() => detailItems.find(item => item.GUID === activeTab), [detailItems, activeTab]);

    const sections = useMemo(() => {
        if (!activeItem) return [];

        // Use DisplayContent for localized data
        const contentStr = activeItem.DisplayContent || activeItem.Content || '';
        const maintenanceEn = activeItem.MaintenanceEn || '';
        const isMileageGrouped = maintenanceEn === 'SUPRA' || maintenanceEn === 'URBAN CRUISER';

        if (isMileageGrouped) {
            const groupBlocks = contentStr.split(/(?=\d-)/).filter(b => b.trim());

            return groupBlocks.map(block => {
                const lines = block.split(/\r?\n/).map(l => l.trim()).filter(l => l);
                if (lines.length === 0) return null;

                const title = lines[0].replace(/^\d-\s*/, '').trim();
                const subgroups = [];
                let currentSub = null;

                lines.slice(1).forEach(line => {
                    const isBullet = line.startsWith('-') || line.startsWith('•');
                    if (!isBullet) {
                        if (currentSub) subgroups.push(currentSub);
                        currentSub = { title: line.replace(/:$/, ''), items: [] };
                    } else if (currentSub) {
                        currentSub.items.push(line.replace(/^[-•]\s*/, ''));
                    } else {
                        currentSub = { title: '', items: [line.replace(/^[-•]\s*/, '')] };
                    }
                });
                if (currentSub) subgroups.push(currentSub);
                return { title, subgroups };
            }).filter(Boolean);
        } else {
            const lines = contentStr.split(/\r?\n/).map(l => l.trim()).filter(l => l);
            const result = [];
            let currentSection = null;

            lines.forEach(line => {
                const isHeader = /^\d-/.test(line);
                if (isHeader) {
                    if (currentSection) result.push(currentSection);
                    const title = line.replace(/^\d-\s*/, '');
                    currentSection = { title, items: [] };
                } else if (currentSection) {
                    currentSection.items.push(line.replace(/^[-•]\s*/, ''));
                }
            });

            if (currentSection) result.push(currentSection);
            return result;
        }
    }, [activeItem]);

    const parseNoteTable = (content) => {
        if (!content) return [];
        const blocks = content.split(/\r?\n\r?\n/).filter(b => b.trim());
        return blocks.map(block => {
            const lines = block.split(/\r?\n/).map(l => l.trim()).filter(l => l);
            if (lines.length === 0) return null;
            const title = lines[0].replace(/:$/, '');
            const details = lines.slice(1).map(l => l.replace(/^[-•]\s*/, ''));
            return { title, details };
        }).filter(Boolean);
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div>;
    }

    return (
        <div className={`maintenance-page ${isRtl ? 'rtl' : 'ltr'} pb-24`} dir={isRtl ? 'rtl' : 'ltr'}>
            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .maintenance-page {
                    font-family: 'Inter', 'Roboto', 'Arial', sans-serif;
                    color: #1a1a1a;
                    width: 100%;
                }
                
                .maintenance-mini-banner {
                    width: 100%;
                    height: 240px;
                    border-radius: 20px;
                    overflow: hidden;
                    margin-bottom: 3.5rem;
                    position: relative;
                    background: #111;
                    box-shadow: 0 15px 45px rgba(0,0,0,0.12);
                }
                .banner-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(${isRtl ? 'to left' : 'to right'}, rgba(0,0,0,0.85), transparent 80%);
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    padding: 0 4rem;
                }
                .banner-text-content {
                    animation: fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
                }
                .banner-title-main { 
                    color: #fff; font-size: 3rem; font-weight: 950; margin-bottom: 0.5rem; letter-spacing: -0.8px;
                }
                .banner-subtitle-main {
                    color: #40d35d; font-size: 2rem; font-weight: 900; text-transform: uppercase;  
                }
                .banner-img { width: 100%; height: 100%; object-fit: cover; opacity: 0.85; }

                .tabs-container {
                    margin-bottom: 4rem; background: #fff; position: sticky; top: 0; z-index: 10; padding: 1.5rem 0; border-bottom: 1px solid #f2f2f2;
                }
                .tabs-scroll {
                    display: flex; overflow-x: auto; white-space: nowrap; -ms-overflow-style: none; scrollbar-width: none;
                    justify-content: ${detailItems.length > 5 ? 'flex-start' : 'center'}; padding: 0 2rem;
                }
                .tabs-scroll::-webkit-scrollbar { display: none; }
                .tab-btn {
                    padding: 1rem 2.5rem; border: none; background: none; font-weight: 800; font-size: 1.8rem; color: #b0b0b0;
                    cursor: pointer; position: relative; text-transform: uppercase; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .tab-btn:hover { color: #40d35d; }
                .tab-btn.active { color: #40d35d; font-size: 1.9rem; }
                .tab-btn.active::after {
                    content: ''; position: absolute; bottom: 0; left: 20%; right: 20%; height: 6px; background: #40d35d; border-radius: 10px;
                }

                .accordion-container { width: 100%; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
                .accordion-item { 
                    border: 1px solid #f2f2f2; background: #fff; border-radius: 16px; overflow: hidden; 
                    transition: box-shadow 0.3s ease, border-color 0.3s ease;
                }
                .accordion-item.open { box-shadow: 0 12px 30px rgba(0,0,0,0.06); border-color: #eee; }
                
                .accordion-header {
                    width: 100%; display: flex; justify-content: space-between; align-items: center; padding: 2rem 3rem; background: #fff; border: none; cursor: pointer; text-align: inherit;
                }
                .header-main-title { font-size: 2.2rem; font-weight: 950; color: #1a1a1a; transition: color 0.3s ease; }
                .accordion-item.open .header-main-title { color: #40d35d; }
                
                .accordion-arrow { 
                    transition: transform 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28), background 0.3s, color 0.3s; 
                    color: var(--primary); background: #40d35dff; padding: 4px; border-radius: 50%;
                }
                .accordion-item.open .accordion-arrow { transform: rotate(180deg); background: var(--primary); color: #fff; }
                
                .accordion-content {
                    max-height: 0; 
                    visibility: hidden; 
                    overflow: hidden; 
                    transition: max-height 0.3s cubic-bezier(0, 1, 0, 1), visibility 0s 0.3s; 
                    background: #fff;
                }
                .accordion-item.open .accordion-content { 
                    max-height: 1500px; 
                    visibility: visible; 
                    transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s; 
                    padding: 0 3rem 3.5rem; 
                }
                
                .subgroup-label {
                    font-weight: 950; color: #1a1a1a; margin-bottom: 2rem; display: block; font-size: 1.5rem; text-transform: uppercase; border-bottom: 3px solid #f9f9f9; padding-bottom: 12px;
                    margin-top: 3rem;
                }
                .subgroup-label:first-child { margin-top: 5; }

                .task-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(450px, 1fr)); gap: 1.5rem 4rem; padding-top: 1rem; }
                .task-item { 
                    display: flex; align-items: flex-start; font-size: 1.7rem; color: #444; line-height: 1.6; font-weight: 600;
                    animation: fadeInUp 0.4s backwards;
                }
                .task-item::before { 
                    content: '•'; color: #40d35d; font-weight: 950; font-size: 2rem; line-height: 1; margin-${isRtl ? 'left' : 'right'}: 16px; margin-top: -3px;
                }

                .note-header { 
                    font-size: 2.2rem; font-weight: 950; margin: 8rem 0 3.5rem; color: #1a1a1a; padding-${isRtl ? 'right' : 'left'}: 2rem; border-${isRtl ? 'right' : 'left'}: 10px solid #40d35d;
                }
                .note-table { 
                    width: 40%; background: #fff; border: 1px solid #eee; border-radius: 20px; overflow: hidden; box-shadow: 0 15px 50px rgba(0,0,0,0.05);
                }
                .note-row { display: flex; border-bottom: 1px solid #f5f5f5; }
                .note-cell-label { 
                    flex: 1; padding: 3rem; background: #fafafa; font-weight: 950; color: #1a1a1a; border-${isRtl ? 'left' : 'right'}: 1px solid #f0f0f0; font-size: 2rem; align-items: center; justify-content: start; display: flex;
                }
                .note-cell-val { flex: 2; padding: 3rem; display: flex; flex-direction: column; gap: 1rem; align-items: start; justify-content: start; font-size: 2.2rem; }
                .note-bullet { margin-bottom: 1.25rem; font-size: 1.6rem; color: #4d4d4d; display: flex; align-items: center; font-weight: 600; }
                .note-bullet::before { content: '—'; margin-${isRtl ? 'left' : 'right'}: 15px; color: #40d35d; font-weight: 950; }

                .bullet-list { list-style: none; padding: 0; }
                .bullet-list-item { 
                    position: relative; padding-${isRtl ? 'right' : 'left'}: 4rem; margin-bottom: 2rem; font-size: 1.6rem; color: #333; line-height: 1.8; font-weight: 600;
                    animation: fadeInUp 0.5s backwards;
                }
                .bullet-list-item::before { content: '•'; position: absolute; ${isRtl ? 'right' : 'left'}: 0; color: #40d35d; font-weight: 950; font-size: 2.5rem; top: -6px; }

                .important-box { 
                    background: #fff; border: 3px solid #40d35d; border-radius: 20px; padding: 4rem; margin-top: 8rem; position: relative; box-shadow: 0 25px 60px rgba(64, 211, 93, 0.12);
                }
                .important-label { color: #40d35d; font-size: 2rem; font-weight: 950; margin-bottom: 2rem; text-transform: uppercase; }
                .important-text { font-size: 1.5rem; color: #1a1a1a; line-height: 1.9; font-weight: 600; }

                @media (max-width: 991px) {
                    .tabs-scroll { justify-content: flex-start; padding: 0 1.5rem; }
                    .tab-btn { font-size: 1.6rem; padding: 1rem 1.2rem; }
                    .header-main-title { font-size: 1.8rem; }
                    .task-grid { grid-template-columns: 1fr; gap: 1rem; }
                    .note-table { width: 100%; border-radius: 15px; }
                    .note-row { flex-direction: column; }
                    .note-cell-label { 
                        padding: 1.5rem 2rem; border: none; border-bottom: 1px solid #f0f0f0; font-size: 1.8rem;
                    }
                    .note-cell-val { padding: 2rem; font-size: 1.8rem; }
                    .note-bullet { font-size: 1.5rem; margin-bottom: 0.8rem; }
                    .banner-overlay { padding: 0 2rem; }
                    .banner-title-main { font-size: 2.4rem; } 
                    .accordion-header { padding: 1.5rem 2rem; }
                    .maintenance-mini-banner { height: 160px; margin-bottom: 2rem; }
                    .important-box { padding: 2.5rem; margin-top: 4rem; }
                }

                /* Dark Mode Override */
                body.dark .maintenance-page { color: #f4f4f5; }
                body.dark .tabs-container { background: #18181b; border-bottom: 1px solid rgba(255,255,255,0.05); }
                body.dark .tab-btn { color: #666; }
                body.dark .tab-btn.active { color: #40d35d; }
                body.dark .active::after { background: #40d35d; }
                
                body.dark .accordion-item { background: #18181b; border-color: rgba(255,255,255,0.05); margin-bottom: 16px; }
                body.dark .accordion-item.open { box-shadow: 0 12px 30px rgba(0,0,0,0.3); border-color: rgba(255,255,255,0.1); }
                body.dark .accordion-header { background: #18181b; border: none; }
                body.dark .header-main-title { color: #f4f4f5; }
                body.dark .accordion-item.open .header-main-title { color: #40d35d; }
                body.dark .accordion-content { background: #18181b; }
                body.dark .subgroup-label { color: #f4f4f5; border-bottom: 3px solid rgba(255,255,255,0.05); }
                body.dark .task-item { color: #a1a1aa; }
                body.dark .task-item::before { color: #40d35d; }
                
                body.dark .note-header { color: #f4f4f5; border-color: #40d35d; }
                body.dark .note-table { background: #18181b; border: 1px solid rgba(255,255,255,0.05); box-shadow: 0 15px 50px rgba(0,0,0,0.3); }
                body.dark .note-row { border-bottom: 1px solid rgba(255,255,255,0.05); }
                body.dark .note-cell-label { background: #27272a; color: #f4f4f5; border-color: rgba(255,255,255,0.05); }
                body.dark .note-bullet { color: #a1a1aa; }
                body.dark .note-bullet::before { color: #40d35d; }
                
                body.dark .bullet-list-item { color: #a1a1aa; }
                body.dark .bullet-list-item::before { color: #40d35d; }
                
                body.dark .important-box { background: #18181b; border: 3px solid #40d35d; box-shadow: 0 25px 60px rgba(64,211,93,0.12); }
                body.dark .important-label { color: #40d35d; }
                body.dark .important-text { color: #a1a1aa; }
            `}</style>

            <div className="maintenance-mini-banner">
                <img src="/assets/images/maintenance_banner.png" alt="Toyota Service" className="banner-img" onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=80&w=2000&auto=format&fit=crop';
                }} />
                <div className="banner-overlay">
                    <div className="banner-text-content" key={activeTab}>
                        <h2 className="banner-title-main">{activeItem?.DisplayMaintenance || activeItem?.MaintenanceAr || 'الصيانة الدورية'}</h2>
                        {(activeItem?.DisplaySubTitle || activeItem?.SubTitle) && (
                            <p className="banner-subtitle-main">{activeItem.DisplaySubTitle || activeItem.SubTitle}</p>
                        )}
                    </div>
                </div>
            </div>

            <div className="tabs-container">
                <div className="tabs-scroll">
                    {detailItems.map((item, idx) => (
                        <button
                            key={item.GUID}
                            className={`tab-btn ${activeTab === item.GUID ? 'active' : ''}`}
                            onClick={() => {
                                setActiveTab(item.GUID);
                                setOpenAccordion(0);
                            }}
                        >
                            {activeItem?.GUID === item.GUID ? (item.DisplayMaintenance || item.MaintenanceAr) : (item.DisplayMaintenance || (lang === 'ar' ? item.MaintenanceAr : item.MaintenanceEn))}
                        </button>
                    ))}
                </div>
            </div>

            <div className="accordion-container">
                {sections.map((section, idx) => (
                    <div key={idx} className={`accordion-item ${openAccordion === idx ? 'open' : ''}`}>
                        <button className="accordion-header" onClick={() => setOpenAccordion(openAccordion === idx ? -1 : idx)}>
                            <span className="header-main-title">{section.title}</span>
                            <span className="accordion-arrow">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" color='#fff' stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </span>
                        </button>
                        <div className="accordion-content">
                            {section.subgroups ? (
                                section.subgroups.map((sub, sIdx) => (
                                    <div key={sIdx} className="subgroup-box">
                                        {sub.title && <span className="subgroup-label">{sub.title}</span>}
                                        <div className="task-grid">
                                            {sub.items.map((item, i) => (
                                                <div key={i} className="task-item" style={{ animationDelay: `${i * 0.02}s` }}>{item}</div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="subgroup-box">
                                    <div className="task-grid">
                                        {section.items.map((item, i) => (
                                            <div key={i} className="task-item" style={{ animationDelay: `${i * 0.02}s` }}>{item}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="notes-wrapper">
                {noteItems.map((note) => {
                    const noteTitle = note.DisplayTitle || note.Title;
                    const noteContent = note.DisplayContent || note.Content;

                    if (note.Id === 1) {
                        const tableData = parseNoteTable(noteContent);
                        return (
                            <div key={note.GUID}>
                                <h3 className="note-header">{noteTitle}</h3>
                                <div className="note-table">
                                    {tableData.map((row, rIdx) => (
                                        <div key={rIdx} className="note-row">
                                            <div className="note-cell-label">{row.title}</div>
                                            <div className="note-cell-val">
                                                {row.details.map((det, dIdx) => (
                                                    <div key={dIdx} className="note-bullet">{det}</div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }

                    if (note.Id === 2 || note.Id === 3) {
                        const lines = noteContent?.split(/\r?\n/).map(l => l.trim()).filter(l => l);
                        return (
                            <div key={note.GUID}>
                                <h3 className="note-header">{noteTitle}</h3>
                                <ul className="bullet-list">
                                    {lines.map((line, lIdx) => (
                                        <li key={lIdx} className="bullet-list-item">{line.replace(/^[-•]\s*/, '')}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                    }

                    if (note.Id === 4) {
                        return (
                            <div key={note.GUID} className="important-box">
                                <h3 className="important-label">{noteTitle}</h3>
                                <p className="important-text">{noteContent}</p>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
};

export default PeriodicMaintenance;
